/**
 * OAuth 2.0 utilities for Google and Apple redirect-based authentication.
 */

const GOOGLE_AUTH_ENDPOINT = 'https://accounts.google.com/o/oauth2/v2/auth';
const APPLE_AUTH_ENDPOINT = 'https://appleid.apple.com/auth/authorize';
const OAUTH_NONCE_KEY = 'oauth-nonce';
const OAUTH_RETURN_PATH_KEY = 'oauth-return-path';

/**
 * Generates a cryptographically random nonce string.
 */
function generateNonce(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Builds the Google OAuth 2.0 authorization URL and redirects the browser.
 *
 * @param clientId - Google OAuth client ID
 * @param returnPath - hash path to return to after auth (e.g. '#/sessions/abc')
 */
export async function redirectToGoogleAuth(clientId: string, returnPath?: string): Promise<void> {
  const isNative = typeof (window as any).Capacitor !== 'undefined' && (window as any).Capacitor?.isNativePlatform?.();
  if (isNative) {
    return;
  }

  const nonce = generateNonce();
  sessionStorage.setItem(OAUTH_NONCE_KEY, nonce);
  sessionStorage.setItem(OAUTH_RETURN_PATH_KEY, returnPath || '#/');

  const redirectUri = `${window.location.origin}/auth/callback`;

  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'id_token',
    scope: 'openid email profile',
    nonce,
    prompt: 'select_account',
  });

  window.location.href = `${GOOGLE_AUTH_ENDPOINT}?${params.toString()}`;
}

/**
 * Builds the Apple Sign-In authorization URL and redirects the browser.
 * Apple requires form_post response_mode when requesting name/email scopes,
 * so the callback points directly to the backend endpoint /auth/apple/callback.
 *
 * @param clientId - Apple Services ID (e.g. 'com.example.app.web')
 * @param returnPath - hash path to return to after auth
 * @param linkUserId - optional user ID if initiating an account linking flow
 */
export async function redirectToAppleAuth(clientId: string, returnPath?: string, linkUserId?: string): Promise<void> {
  const isNative = typeof (window as any).Capacitor !== 'undefined' && (window as any).Capacitor?.isNativePlatform?.();
  if (isNative) {
    return;
  }

  const nonce = generateNonce();
  sessionStorage.setItem(OAUTH_NONCE_KEY, nonce);
  sessionStorage.setItem(OAUTH_RETURN_PATH_KEY, returnPath || '#/');

  const redirectUri = `${window.location.origin}/auth/apple/callback`;
  const state = linkUserId ? `link:${linkUserId}|${returnPath || '#/'}` : (returnPath || '#/');

  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'code id_token',
    response_mode: 'form_post',
    scope: 'name email',
    nonce,
    state,
  });

  window.location.href = `${APPLE_AUTH_ENDPOINT}?${params.toString()}`;
}

/**
 * Result from parsing the OAuth callback URL fragment.
 */
export interface OAuthCallbackResult {
  /** The OAuth ID token (JWT). */
  idToken?: string;
  /** The provider name ('google' | 'apple') */
  provider?: string;
  /** The hash path to navigate to after login. */
  returnPath: string;
  /** Linking result or error if present */
  linked?: string;
  error?: string;
}

/**
 * Parses the OAuth callback from the current URL fragment.
 *
 * Callback examples:
 *   Google: /auth/callback#id_token=TOKEN&...
 *   Apple:  /auth/callback#id_token=TOKEN&provider=apple&return_path=#/
 *   Linked: /auth/callback#linked=apple&return_path=#/settings
 */
export function parseOAuthCallback(): OAuthCallbackResult | null {
  const hash = window.location.hash;
  if (!hash || hash.length < 2) return null;

  // Parse fragment parameters (strip the leading '#')
  const params = new URLSearchParams(hash.substring(1));
  const idToken = params.get('id_token');
  const provider = params.get('provider') || 'google';
  const linked = params.get('linked') || undefined;
  const error = params.get('error') || undefined;
  const explicitReturnPath = params.get('return_path');

  if (linked || error) {
    const returnPath = explicitReturnPath || sessionStorage.getItem(OAUTH_RETURN_PATH_KEY) || '#/';
    sessionStorage.removeItem(OAUTH_RETURN_PATH_KEY);
    return { linked, error, returnPath, provider };
  }

  if (!idToken) return null;

  // Validate nonce for Google implicit flow (Apple token nonce is checked server-side)
  if (provider === 'google') {
    try {
      const payloadB64 = idToken.split('.')[1];
      const payload = JSON.parse(atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/')));
      const storedNonce = sessionStorage.getItem(OAUTH_NONCE_KEY);
      if (storedNonce && payload.nonce && payload.nonce !== storedNonce) {
        console.error('OAuth nonce mismatch');
        return null;
      }
    } catch {
      console.error('Failed to validate OAuth nonce');
      return null;
    }
  }

  // Clean up stored nonce (single-use)
  sessionStorage.removeItem(OAUTH_NONCE_KEY);

  const returnPath = explicitReturnPath || sessionStorage.getItem(OAUTH_RETURN_PATH_KEY) || '#/';
  sessionStorage.removeItem(OAUTH_RETURN_PATH_KEY);

  return { idToken, provider, returnPath };
}

/**
 * Returns true if the current page is the OAuth callback route.
 */
export function isOAuthCallback(): boolean {
  return window.location.pathname === '/auth/callback';
}

/**
 * Directly exchanges a raw credential (e.g. 1-hour Google ID token) for a 30-day session token.
 */
export async function exchangeSessionTokenDirect(
  token: string,
  clientName: string,
  apiBaseUrl: string = ''
): Promise<{ token: string; expiresAt?: string }> {
  if (token.startsWith('jt_at_')) {
    return { token };
  }
  const lower = clientName.toLowerCase();
  const clientId = lower.includes('portal')
    ? 'telemetry-portal'
    : lower.includes('editor')
    ? 'telemetry-editor'
    : 'telemetry-extractor';
  try {
    const resp = await fetch(`${apiBaseUrl}/rpc/ExchangeSessionToken`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ clientId, clientName }),
    });
    if (resp.ok) {
      const data = await resp.json();
      if (data.token) {
        return {
          token: data.token,
          expiresAt: data.expiresAt ? new Date(data.expiresAt).getTime().toString() : undefined,
        };
      }
    }
  } catch (err) {
    console.warn('exchangeSessionTokenDirect failed:', err);
  }
  return { token };
}

/**
 * Handles processing an OAuth callback on application entry. If a raw ID token is received,
 * it is exchanged immediately for a 30-day session token before persisting to localStorage.
 */
export async function handleOAuthCallback(clientName: string, apiBaseUrl: string = ''): Promise<boolean> {
  if (!isOAuthCallback()) return false;
  const result = parseOAuthCallback();
  if (result && result.idToken) {
    const { token, expiresAt } = await exchangeSessionTokenDirect(result.idToken, clientName, apiBaseUrl);
    localStorage.setItem('auth-token', token);
    if (expiresAt) {
      localStorage.setItem('auth-token-expires-at', expiresAt);
    }
    window.location.replace(window.location.origin + '/' + result.returnPath);
    return true;
  } else if (result && (result.linked || result.error)) {
    window.location.replace(window.location.origin + '/' + result.returnPath);
    return true;
  } else {
    window.location.replace(window.location.origin + '/#/login');
    return true;
  }
}
